/* Still Frozen — H-04 «Your delivery date» (E03v6; D-06 ship days by transit since 2026-10-19).
 *
 * Date rule + ZIP classification for the home-page promise block. No geolocation, no timers.
 * Rule — decision D-06 (owner 2026-10-19, docs/decisions.md) on bible §1.1 «Скорость» and H-04: cut-off 14:00 ET on
 * every ship day (America/New_York); the ship days depend on the transit to the shopper's ZIP:
 *   transit 2 (default): Mon–Wed. Mon <14:00 → Mon · Mon ≥14:00 → Tue · Tue <14:00 → Tue · Tue ≥14:00 → Wed ·
 *     Wed <14:00 → Wed · Wed ≥14:00, Thu, Fri, Sat, Sun → next Mon; arrival = ship + 2 (Mon→Wed, Tue→Thu, Wed→Fri).
 *   transit 1 (the live 2-day-or-faster service to that ZIP arrives in 1 day): Thursday too — Wed ≥14:00 → Thu ·
 *     Thu <14:00 → Thu; arrival = ship + 1. Never a Friday or weekend ship.
 * The transit comes from the live rate service (owner 2026-10-19 15:48: срок 1/2 дня по ZIP — из живого ответа Veeqo):
 * on submit the widget asks window.SFLive.get(zip) (assets/sf-v2-live-rates.js, cached per ZIP and day) and passes
 * SFArrival.fromRates(answer) on. No answer (service down, rates off, script absent) → the 2-day rule: never a guessed
 * Thursday. Output: «Ships Wednesday → arrives Friday, Oct 2» (bible H-04: `Ships [weekday] → arrives [weekday, date]`).
 *
 * ZIP: five digits (ZIP+4 accepted on its first five). Refused as outside the Lower 48 by public USPS 3-digit
 * prefixes: AK 995–999 · HI 967–968 · PR/USVI 006–009 · APO/FPO/DPO 090–098, 340, 962–966 · Guam/CNMI/AS 969.
 * Everything else is treated as the 48 contiguous states + DC; the widget claims nothing more than that.
 *
 * The same shipOffset lives in assets/sf-v2-arrival.js (cards / PDP / cart / gift calendar) since E23v6;
 * tools/test-e23-gifts.py keeps the two bodies identical and both modules equal on a 2 000-instant grid.
 * Since E23v6 r2 compute() delegates to window.SFArrival when it is on the page (the section prints its script tag),
 * so holidays and the owner's closed dates (data-sf-dd-closed) are honoured; without it the rule above applies.
 * The HTML always carries the literal fallback «Frozen orders ship Mon–Wed» in the status region; this script only
 * replaces it after a submit, and only with a computed date or one of the fixed sentences. Never an empty string,
 * never a guessed date. window.SFDeliveryDate.compute(date, opts) / classifyZip(text) are pure (no DOM) so every instant
 * and input is testable without a real clock — see tools/test-e03-delivery-date.py.
 */
(function () {
  'use strict';
  var root = typeof window !== 'undefined' ? window : this;
  if (root.SFDeliveryDate && root.SFDeliveryDate.version) return;

  var TIME_ZONE = 'America/New_York';
  var CUTOFF_MINUTES = 14 * 60; // 14:00:00 ET itself already counts as past the cut-off
  var TRANSIT_DAYS = 2;
  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var DAY_MS = 86400000;

  var COPY = {
    fallback: 'Frozen orders ship Mon–Wed',
    invalid: 'Please enter a 5-digit ZIP code.',
    /* bible §4.10 shipping page, verbatim */
    outside: 'We do not currently ship frozen orders to Alaska, Hawaii, Puerto Rico, US territories, military addresses, or PO boxes.'
  };

  /* [from, to] inclusive ranges of the first three ZIP digits that are not the Lower 48 + DC */
  var OUTSIDE_PREFIXES = [[6, 9], [90, 98], [340, 340], [962, 966], [967, 968], [969, 969], [995, 999]];

  /* Calendar components of `date` as seen in New York; null when the runtime cannot express the zone. */
  function etParts(date) {
    if (!(date instanceof Date) || isNaN(date.getTime())) return null;
    if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) return null;
    var fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: TIME_ZONE, hour12: false,
      year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric'
    });
    if (typeof fmt.formatToParts !== 'function') return null;
    var p = {};
    fmt.formatToParts(date).forEach(function (part) { p[part.type] = part.value; });
    var year = +p.year, month = +p.month, day = +p.day, hour = +p.hour % 24, minute = +p.minute;
    if (![year, month, day, hour, minute].every(isFinite)) return null;
    var utcMidnight = Date.UTC(year, month - 1, day); // the ET calendar day as a UTC stamp — DST-proof arithmetic
    return { weekday: new Date(utcMidnight).getUTCDay(), minutes: hour * 60 + minute, utcMidnight: utcMidnight };
  }

  /* Days from the current ET calendar day to the next ship day. Pure; exported for the sweep in the test. */
  function shipOffset(weekday, minutes, transit) {
    var pastCutoff = minutes >= CUTOFF_MINUTES;
    var thursday = transit === 1;
    switch (weekday) {
      case 1: return pastCutoff ? 1 : 0;   // Monday: today, or Tuesday after 14:00
      case 2: return pastCutoff ? 1 : 0;   // Tuesday: today, or Wednesday after 14:00
      case 3: return pastCutoff ? (thursday ? 1 : 5) : 0;   // Wednesday: today; after 14:00 Thursday (1-day) or next Monday
      case 4: return thursday && !pastCutoff ? 0 : 4;      // Thursday: today on the 1-day rule before 14:00, else Monday
      case 5: return 3;                    // Friday → Monday
      case 6: return 2;                    // Saturday → Monday
      case 0: return 1;                    // Sunday → Monday
    }
    return null;
  }

  function format(utcMidnight) {
    var d = new Date(utcMidnight);
    return { weekday: DAYS[d.getUTCDay()], month: MONTHS[d.getUTCMonth()], day: d.getUTCDate(), iso: d.toISOString().slice(0, 10) };
  }

  /* Pure: instant → { ship, arrival, label } or null when nothing trustworthy can be said. opts.transit 1 | 2 (D-06). */
  function compute(now, opts) {
    try {
      var shared = root.SFArrival;
      if (shared && shared.version >= 2 && typeof shared.compute === 'function') {
        // E23v6 r2: the shared engine knows the closed days (six national holidays + the theme setting); when it is on
        // the page the widget repeats ITS answer, so the home page never shows two different arrival days.
        var r = shared.compute(now, opts);
        if (!r) return null;
        return { ship: r.ship, arrival: r.arrival,
                 label: 'Ships ' + r.ship.weekday + ' → arrives ' + r.arrival.weekday + ', ' + r.arrival.month + ' ' + r.arrival.day };
      }
      var et = etParts(now === undefined ? new Date() : now);
      if (!et) return null;
      var transit = opts && opts.transit === 1 ? 1 : TRANSIT_DAYS;
      var offset = shipOffset(et.weekday, et.minutes, transit);
      if (offset === null) return null;
      var ship = format(et.utcMidnight + offset * DAY_MS);
      var arrival = format(et.utcMidnight + (offset + transit) * DAY_MS);
      return {
        ship: ship, arrival: arrival,
        label: 'Ships ' + ship.weekday + ' → arrives ' + arrival.weekday + ', ' + arrival.month + ' ' + arrival.day
      };
    } catch (e) {
      return null;
    }
  }

  /* Pure: raw field text → { ok: true, zip } | { ok: false, reason: 'invalid' | 'outside' } */
  function classifyZip(raw) {
    var text = typeof raw === 'string' ? raw.trim() : '';
    if (!/^\d{5}(-\d{4})?$/.test(text)) return { ok: false, reason: 'invalid' };
    var zip = text.slice(0, 5);
    var prefix = +zip.slice(0, 3);
    for (var i = 0; i < OUTSIDE_PREFIXES.length; i++) {
      if (prefix >= OUTSIDE_PREFIXES[i][0] && prefix <= OUTSIDE_PREFIXES[i][1]) return { ok: false, reason: 'outside', zip: zip };
    }
    return { ok: true, zip: zip };
  }

  /* DOM: one form → status text. Returns the state written. opts.transit — the ZIP's transit (1 | 2), else 2. */
  function render(form, now, opts) {
    var input = form.querySelector('input[name="zip"]');
    var status = form.parentNode.querySelector('[data-sf-dd-status]') || form.querySelector('[data-sf-dd-status]');
    if (!input || !status) return null;
    var zip = classifyZip(input.value);
    var state, text;
    if (!zip.ok) {
      state = zip.reason;
      text = COPY[zip.reason];
    } else {
      var result = compute(now, { closed: form.getAttribute('data-sf-dd-closed') || '', transit: (opts && opts.transit) || TRANSIT_DAYS });
      state = result ? 'ok' : 'fallback';
      text = result ? result.label : COPY.fallback;
    }
    status.textContent = text;
    status.setAttribute('data-state', state);
    if (state === 'invalid') {
      input.setAttribute('aria-invalid', 'true');
      input.focus();
    } else {
      input.removeAttribute('aria-invalid');
    }
    return state;
  }

  /* The ZIP's transit from the live rate service (D-06): a Promise of 1 | 2; 2 whenever there is no answer. */
  function transitFor(zip) {
    var live = root.SFLive, eng = root.SFArrival;
    if (!live || typeof live.get !== 'function' || !eng || typeof eng.fromRates !== 'function') return Promise.resolve(TRANSIT_DAYS);
    return live.get(zip).then(function (d) { return d ? eng.fromRates(d) : TRANSIT_DAYS; }, function () { return TRANSIT_DAYS; });
  }

  function wire() {
    var forms = document.querySelectorAll('form[data-sf-delivery-date]:not([data-sf-dd-wired])');
    Array.prototype.forEach.call(forms, function (form) {
      form.setAttribute('data-sf-dd-wired', '');
      form.addEventListener('submit', function (ev) {
        ev.preventDefault();
        // the 2-day rule answers at once (it holds for every ZIP); a 1-day ZIP moves the date up when the live answer lands
        render(form, undefined, { transit: TRANSIT_DAYS });
        var input = form.querySelector('input[name="zip"]'), zip = classifyZip(input ? input.value : '');
        if (!zip.ok || typeof Promise === 'undefined') return;
        var asked = input.value;
        transitFor(zip.zip).then(function (t) { if (t === 1 && input.value === asked) render(form, undefined, { transit: 1 }); });
      });
    });
  }

  root.SFDeliveryDate = { version: '1', compute: compute, classifyZip: classifyZip, shipOffset: shipOffset, render: render, COPY: COPY };

  if (typeof document !== 'undefined' && document && document.querySelectorAll) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire);
    else wire();
    if (document.addEventListener) document.addEventListener('shopify:section:load', wire);
  }
})();
